/* eslint-disable arrow-body-style */
import { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

const getData = async ({
  reportType,
  cohortId,
  feedbackFormId,
  pivotMode,
  rowGroupCols,
  pivotCols,
  filterModel,
  limit = 100000,
  companyId,
  location,
}) => {
  const url = endpoints.performanceReview.insights.generateReport;

  const res = await sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      reportType,
      cohortId,
      limit,
      baseFilter: {
        feedbackFormId,
        location,
        companyId,
      },
      pivotMode,
      rowGroupCols,
      pivotCols,
      filterModel,
    },
  });

  return res?.entity;
};

export const useGetEngagementCompletionStatus = ({ rowGroupCols, feedbackFormId, companyId, location, reportType }) => {
  return useQuery({
    queryKey: [
      REACT_QUERY_KEYS.engagementCompletionStatus,
      rowGroupCols,
      feedbackFormId,
      companyId,
      location,
      reportType,
    ],
    queryFn: () =>
      getData({
        reportType,
        rowGroupCols,
        feedbackFormId,
        cohortId: -1,
        pivotCols: [],
        pivotMode: false,
        filterModel: {},
        limit: null,
        companyId,
        location,
      }),
    enabled: !!feedbackFormId,
  });
};

export const useCompletionStatusMetricCardsDivisionView = ({ gridRef }) => {
  const [metricsCardsData, setMetricsCardsData] = useState([]);

  const onModelUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });

    // sum of PARTICIPANT_COUNT
    const totalSent = newRowData.reduce((acc, row) => {
      return acc + (row?.PARTICIPANT_COUNT || 0);
    }, 0);

    // sum of PENDING_COUNT
    const totalPending = newRowData.reduce((acc, row) => {
      return acc + (row?.PENDING_COUNT || 0);
    }, 0);

    const totalResponded = newRowData.reduce((acc, row) => {
      return acc + (row?.RESPONDED_COUNT || 0);
    }, 0);

    const responsePercentage = totalResponded > 0 ? ((totalResponded / totalSent) * 100).toFixed(2) : 0;

    setMetricsCardsData([
      {
        cardId: 'TOTAL_SENT',
        title: 'Total sent',
        value: totalSent,
      },
      {
        cardId: 'RESPONSE_PERCENTAGE',
        title: 'Response %',
        value: responsePercentage,
        subTitle: `Response received: ${totalResponded}`,
      },
      {
        cardId: 'TOTAL_PENDING',
        title: 'Total pending',
        value: totalPending,
      },
    ]);
  }, [gridRef]);

  return {
    metricsCardsData,
    onModelUpdated,
  };
};

export const useCompletionStatusMetricCardsEmployeeView = ({ gridRef }) => {
  const [metricsCardsData, setMetricsCardsData] = useState([]);

  const onModelUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });

    // sum of PARTICIPANT_COUNT
    const totalSent = newRowData.length;

    // sum of PENDING_COUNT
    const totalPending = newRowData.filter((row) => ['NOT_STARTED', 'IN_PROGRESS'].includes(row?.STATUS)).length;

    const totalResponded = newRowData.filter((row) => row?.STATUS === 'SUBMITTED').length;

    const responsePercentage = totalResponded > 0 ? ((totalResponded / totalSent) * 100).toFixed(2) : 0;

    setMetricsCardsData([
      {
        cardId: 'TOTAL_SENT',
        title: 'Total sent',
        value: totalSent,
      },
      {
        cardId: 'RESPONSE_PERCENTAGE',
        title: 'Response %',
        value: responsePercentage,
        subTitle: `Response received: ${totalResponded}`,
      },
      {
        cardId: 'TOTAL_PENDING',
        title: 'Total pending',
        value: totalPending,
      },
    ]);
  }, [gridRef]);

  return {
    metricsCardsData,
    onModelUpdated,
  };
};

export const notifyEngagementReminderApi = ({ surveyId, employeeIds, functionIds }) => {
  const url = endpoints.engagementV2.remindersNotify;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    queryParams: { survey_id: surveyId },
    body: {
      employeeIds,
      functionIds,
    },
  });
};
